import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Briefcase, Users, Award, Clock, MapPin, Phone, Mail,
  MessageSquare, CheckCircle, Star, Building2, Search, Shield,
  Brain, BarChart3, Flower2, ChevronRight, Loader2, FileText, Target, Mic
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = { Brain, Search, BarChart3, Flower2 };

// Fallback data if API is unavailable
const FALLBACK = {
  hero: {
    badge: 'RECRUTEMENT', title: 'Le Recrutement autrement, en Polynésie', subtitle: 'AERH Recrutement Tahiti',
    description: "25 ans d'expertise locale au service de vos talents.",
    stats: [
      { value: '85%', label: 'de nos placements réussissent leur période d\'essai' },
      { value: '17+', label: 'entreprises partenaires en Polynésie française' },
      { value: '25 ans', label: "d'expertise RH sur le marché polynésien" },
      { value: '100%', label: 'des candidatures traitées avec confidentialité' }
    ],
    cta_primary: { text: 'Diagnostic gratuit', link: '#contact' },
    cta_secondary: { text: 'Découvrir nos offres', link: '#services' }
  },
  références: { title: 'Ils nous font confiance', items: ['Sicar Assurance', 'Rival Peintures', 'Sphere MMA', 'COJP 2027'] },
  packs: { title: 'Des solutions adaptées', subtitle: '', items: [] },
  method: { title: 'La Méthode AERH', subtitle: '', steps: [] },
  assessment: { title: 'Assessment & Évaluation', subtitle: '', cards: [] },
  jdp: { badge: 'JDP TAHITI 2027', title: 'Jeux du Pacifique', description: '', postes: [] },
  tools: { title: 'Vos outils candidats', subtitle: '', apps: [] },
  testimonials: { title: 'Ce que disent nos clients', items: [] },
  contact: { title: 'Parlons de votre prochain recrutement', subtitle: '', phone: '', email: 'contact@groupeaerhpolynesie.com', whatsapp: '' }
};

export const RecruitmentPage = () => {
  const [content, setContent] = useState(FALLBACK);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [form, setForm] = useState({ company: '', name: '', email: '', phone: '', profile: '', pack: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const { data } = await axios.get(`${API}/site-content/recrutement`);
        setContent(prev => ({ ...prev, ...data }));
      } catch (err) {
        console.error('Using fallback content:', err);
      } finally {
        setContentLoaded(true);
      }
    };
    fetchContent();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error('Veuillez remplir les champs obligatoires'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name, email: form.email, phone: form.phone, company: form.company,
        subject: `Recrutement - ${form.pack || 'Non précisé'} - ${form.profile || 'Profil non précisé'}`,
        message: form.message || `Profil recherche: ${form.profile}. Formule: ${form.pack}.`
      });
      toast.success('Demande envoyée ! Nous vous recontactons sous 24h ouvrées.');
      setForm({ company: '', name: '', email: '', phone: '', profile: '', pack: '', message: '' });
    } catch { toast.error('Erreur lors de l\'envoi.'); }
    finally { setSubmitting(false); }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const hero = content.hero || FALLBACK.hero;
  const refs = content.références || FALLBACK.références;
  const packs = content.packs || FALLBACK.packs;
  const method = content.method || FALLBACK.method;
  const assessment = content.assessment || FALLBACK.assessment;
  const jdp = content.jdp || FALLBACK.jdp;
  const tools = content.tools || FALLBACK.tools;
  const testimonials = content.testimonials || FALLBACK.testimonials;
  const contact = content.contact || FALLBACK.contact;

  return (
    <div className="bg-[#F8FAFC]" data-testid="recruitment-page">

      {/* HERO */}
      <section className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] relative overflow-hidden" data-testid="recruitment-hero">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-5">
                <Briefcase className="w-3 h-3" />
                CABINET RH - POLYNÉSIE FRANÇAISE
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6" data-testid="hero-title">
                {hero.title}
              </h1>
              <div className="text-lg text-white/70 leading-relaxed mb-10 max-w-lg" dangerouslySetInnerHTML={{ __html: hero.description }} />
              <div className="flex flex-wrap gap-4">
                <Button onClick={() => scrollTo('contact')} className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-7 py-6" data-testid="cta-diagnostic">
                  {hero.cta_primary?.text || 'Diagnostic gratuit'} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={() => scrollTo('services')} className="border-white/30 text-white hover:border-[#F59E0B] hover:text-[#F59E0B] px-7 py-6" data-testid="cta-formules">
                  {hero.cta_secondary?.text || 'Nos formules'}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-5" data-testid="hero-stats">
              {(hero.stats || []).map((s, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:border-[#F59E0B] transition-colors">
                  <p className="text-3xl font-bold text-[#F59E0B]">{s.value}</p>
                  <p className="text-white/60 text-sm mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REFERENCES */}
      <section className="bg-white py-12 border-b border-gray-100" data-testid="references-section">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-xs uppercase tracking-[0.15em] text-gray-400 mb-8">{refs.title}</p>
          <div className="flex flex-wrap gap-2.5 justify-center max-w-4xl mx-auto">
            {(refs.items || []).map((r) => (
              <span key={r} className="bg-[#F8FAFC] border border-gray-200 rounded-full px-4 py-2 text-sm font-medium text-gray-600 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors">
                {r}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* NOS OFFRES DE MISSION */}
      <section id="services" className="py-20 bg-[#F8FAFC]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
              <Briefcase className="w-3 h-3" /> NOS OFFRES DE MISSION
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937] mb-3">Deux formules, un seul objectif</h2>
            <p className="text-gray-500 leading-relaxed max-w-3xl mx-auto">
              AERH Recrutement vous propose deux niveaux d'accompagnement selon votre disponibilité et vos préférences d'organisation. Notre rigueur méthodologique et notre connaissance du marché polynésien sont mobilisées dès le premier jour.
            </p>
          </div>

          {/* Options A & B */}
          <div className="grid lg:grid-cols-2 gap-8 mb-20">

            {/* OPTION A */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow" data-testid="option-a">
              <div className="bg-[#0D9488] p-8">
                <p className="text-[#F59E0B] text-xs font-bold uppercase tracking-[0.15em] mb-2">OPTION A</p>
                <h3 className="text-2xl font-bold text-white mb-2">Sourcing & Qualification</h3>
                <p className="text-white/70 text-sm">Vous recevez des candidats qualifiés — vous gardez la main sur les entretiens.</p>
              </div>
              <div className="p-8">
                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  AERH prend en charge le sourcing, le tri et la présélection téléphonique des candidatures. Vous recevez 2 à 3 dossiers complets et vérifiés. Les entretiens physiques et la décision finale restent entre vos mains.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { label: 'AERH gère', value: 'Sourcing, tri, présélection' },
                    { label: 'Vous gérez', value: 'Entretiens & décision' },
                    { label: 'Garantie', value: 'Non incluse' },
                    { label: 'Facturation', value: 'Par CV qualifié transmis' },
                  ].map(item => (
                    <div key={item.label} className="bg-[#F8FAFC] rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{item.label}</p>
                      <p className="text-sm text-[#1F2937] font-medium mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>

                <h4 className="text-sm font-bold text-[#1F2937] mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#0D9488]" /> Process en 4 étapes
                </h4>
                <div className="space-y-3">
                  {[
                    { num: '1', title: 'Cadrage', desc: 'Réunion de cadrage, collecte de vos critères, validation de la fiche de poste, calendrier et canaux de diffusion.' },
                    { num: '2', title: 'Sourcing', desc: 'Diffusion multi-canal (site AERH, LinkedIn, Facebook, SEFI). Activation du vivier local. Centralisation des candidatures.' },
                    { num: '3', title: 'Présélection', desc: 'Tri et scoring multicritères. Préqualification téléphonique (15-20 min). Vérification des parcours et prétentions.' },
                    { num: '4', title: 'Livraison', desc: '2 à 3 dossiers candidats qualifiés avec fiche synthèse : scoring, verbatim, coordonnées.' },
                  ].map(step => (
                    <div key={step.num} className="flex gap-3">
                      <div className="w-7 h-7 bg-[#0D9488] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-white">{step.num}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1F2937]">{step.title}</p>
                        <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-amber-800 mb-1">À votre charge ensuite</p>
                  <p className="text-xs text-amber-700 leading-relaxed">Entretiens physiques, vérification des références, contrat de travail, suivi d'intégration.</p>
                </div>
              </div>
            </div>

            {/* OPTION B */}
            <div className="bg-white rounded-2xl border-2 border-[#F59E0B] overflow-hidden shadow-lg relative hover:shadow-xl transition-shadow" data-testid="option-b">
              <span className="absolute top-4 right-4 bg-[#F59E0B] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full z-10">Recommandé</span>
              <div className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] p-8">
                <p className="text-[#F59E0B] text-xs font-bold uppercase tracking-[0.15em] mb-2">OPTION B</p>
                <h3 className="text-2xl font-bold text-white mb-2">Process complet — clé en main</h3>
                <p className="text-white/70 text-sm">Vous déléguez — AERH pilote de la diffusion jusqu'à l'intégration.</p>
              </div>
              <div className="p-8">
                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  AERH pilote l'intégralité du recrutement : diffusion, présélection, entretiens, accompagnement à la décision et suivi d'intégration. Vous n'intervenez qu'au moment de l'arbitrage final.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { label: 'AERH gère', value: 'Intégralité du processus' },
                    { label: 'Vous gérez', value: 'Arbitrage final uniquement' },
                    { label: 'Garantie', value: '3 mois — incluse', highlight: true },
                    { label: 'Facturation', value: 'À la réussite uniquement', highlight: true },
                  ].map(item => (
                    <div key={item.label} className={`rounded-lg p-3 ${item.highlight ? 'bg-teal-50 border border-teal-200' : 'bg-[#F8FAFC]'}`}>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{item.label}</p>
                      <p className={`text-sm font-medium mt-0.5 ${item.highlight ? 'text-[#0D9488]' : 'text-[#1F2937]'}`}>{item.value}</p>
                    </div>
                  ))}
                </div>

                <h4 className="text-sm font-bold text-[#1F2937] mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#0D9488]" /> Process en 5 étapes
                </h4>
                <div className="space-y-3">
                  {[
                    { num: '1', title: 'Cadrage', desc: 'Collecte de vos critères, validation de la fiche de poste, lancement de la mission sous 48h.' },
                    { num: '2', title: 'Sourcing', desc: 'Diffusion multi-canal. Activation du vivier AERH. Centralisation de toutes les candidatures.' },
                    { num: '3', title: 'Présélection', desc: 'Tri et scoring multicritères. Préqualifications structurées. Shortlist de 3 à 5 profils avec recommandation AERH.' },
                    { num: '4', title: 'Entretiens', desc: 'B1 — Co-animation : AERH à vos côtés pendant l\'entretien. B2 — Conduite complète : AERH mène l\'intégralité, compte rendu remis.' },
                    { num: '5', title: 'Finalisation & Suivi', desc: 'Accompagnement négociation salariale. Suivi tripartite J+30, J+60, J+90. Détection anticipée des difficultés.' },
                  ].map(step => (
                    <div key={step.num} className="flex gap-3">
                      <div className="w-7 h-7 bg-[#F59E0B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-white">{step.num}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1F2937]">{step.title}</p>
                        <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <p className="text-xs font-bold text-[#0D9488] mb-1 flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" /> Garantie de remplacement — 3 mois
                  </p>
                  <p className="text-xs text-teal-700 leading-relaxed">En cas de rupture du contrat dans les 3 mois suivant l'embauche, AERH relance une nouvelle recherche sans facturation supplémentaire.</p>
                </div>
              </div>
            </div>
          </div>

          {/* TABLEAU COMPARATIF */}
          <div className="mb-20" data-testid="comparison-table">
            <h3 className="text-2xl font-bold text-[#1F2937] text-center mb-8">Les livrables inclus dans chaque option</h3>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0D9488]">
                    <th className="text-left p-4 text-white font-semibold">Livrable</th>
                    <th className="text-center p-4 text-white font-semibold w-28">Option A</th>
                    <th className="text-center p-4 text-white font-semibold w-28">Option B</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'Fiche de poste validée', a: true, b: true },
                    { name: 'Annonces multi-canal (site AERH, LinkedIn, Facebook, SEFI)', a: true, b: true },
                    { name: 'Centralisation de toutes les candidatures reçues', a: true, b: true },
                    { name: 'Grille de scoring multicritères', a: true, b: true },
                    { name: 'Préqualifications téléphoniques structurées (15-20 min)', a: true, b: true },
                    { name: 'Rapport de shortlist (fiches + scoring + recommandation)', a: true, b: true },
                    { name: "Guide d'entretien personnalisé par candidat", a: false, b: true },
                    { name: 'Présence physique ou conduite des entretiens', a: false, b: true },
                    { name: "Comptes rendus d'entretiens physiques", a: false, b: true },
                    { name: 'Accompagnement de la négociation salariale', a: false, b: true },
                    { name: "Suivi d'intégration J+30 / J+60 / J+90", a: false, b: true },
                    { name: 'Garantie de remplacement 3 mois', a: false, b: true },
                  ].map((row, i) => (
                    <tr key={i} className={`border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}`}>
                      <td className="p-4 text-gray-700">{row.name}</td>
                      <td className="p-4 text-center">
                        {row.a ? <CheckCircle className="w-5 h-5 text-[#0D9488] mx-auto" /> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="p-4 text-center">
                        {row.b ? <CheckCircle className="w-5 h-5 text-[#F59E0B] mx-auto" /> : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* QUELLE OPTION CHOISIR ? */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 lg:p-12 mb-20" data-testid="decision-guide">
            <h3 className="text-2xl font-bold text-[#1F2937] text-center mb-3">Quelle option choisir ?</h3>
            <p className="text-center text-gray-500 mb-8">Un critère simple : votre disponibilité à piloter le processus.</p>
            <div className="grid lg:grid-cols-2 gap-8 mb-10">
              <div className="bg-[#F8FAFC] rounded-xl p-6 border-l-4 border-[#0D9488]">
                <h4 className="font-bold text-[#0D9488] mb-3">Option A — Sourcing</h4>
                <ul className="space-y-2">
                  {[
                    'Implication AERH : Sourcing + présélection',
                    'Entretiens : À votre charge',
                    'Investissement temps : Modéré',
                    'Shortlist : 2 à 3 dossiers qualifiés',
                    'Scoring : Documentaire',
                    'Garantie : Non incluse',
                    'Facturation : Par CV qualifié',
                  ].map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-gray-600">
                      <ChevronRight className="w-3.5 h-3.5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-amber-50 rounded-xl p-6 border-l-4 border-[#F59E0B]">
                <h4 className="font-bold text-[#D97706] mb-3">Option B — Process complet</h4>
                <ul className="space-y-2">
                  {[
                    'Implication AERH : Process 5 phases complet',
                    'Entretiens : Co-animés ou conduits par AERH',
                    'Investissement temps : Minimal — arbitrage final',
                    'Shortlist : 3 à 5 profils + recommandation cabinet',
                    'Scoring : Documentaire + entretien téléphonique',
                    'Garantie : 3 mois — incluse',
                    'Facturation : À la réussite uniquement',
                  ].map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-gray-700">
                      <ChevronRight className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 text-center">
              <p className="text-sm text-teal-800 leading-relaxed">
                <strong>Notre conseil :</strong> Choisissez l'Option A si vous avez l'habitude de conduire des entretiens. Choisissez l'Option B si vous préférez consacrer votre temps à votre activité principale.
                <br /><span className="text-teal-600">Dans les deux cas, la qualité de présélection et la rigueur méthodologique AERH sont identiques.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* POURQUOI AERH */}
      <section className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] py-20" data-testid="why-aerh-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
            NOTRE VALEUR AJOUTÉE
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Pourquoi confier votre recrutement à AERH ?</h2>
          <p className="text-white/60 mb-14 max-w-2xl">Notre ancrage en Polynésie française depuis plus de 25 ans nous confère une connaissance fine du marché local.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Award, title: '25+ ans d\'expérience RH', desc: 'Connaissance fine du tissu économique local, du droit du travail PF, des organismes sociaux (CPS, SEFI) et des usages du marché.' },
              { icon: Users, title: 'Vivier candidats local', desc: 'Base de profils polynésiens identifiés et suivis sur la durée. Activation dès le lancement de la mission.' },
              { icon: BarChart3, title: 'Méthode rigoureuse et objective', desc: 'Grille multicritères pondérée (formation, expérience, aptitudes). Zéro CV non pertinent à trier.' },
              { icon: Shield, title: 'Confidentialité totale', desc: 'Recrutement conduit en votre nom, en toute discrétion. Les candidats écartés reçoivent un retour — votre image est préservée.' },
              { icon: Briefcase, title: 'Accompagnement de A à Z', desc: 'De la fiche de poste au suivi d\'intégration, AERH est votre interlocuteur unique. Négociation salariale incluse.' },
              { icon: MapPin, title: 'Connaissance du terrain PF', desc: 'Clients institutionnels (CHPF, collectivités) et privés à travers toute la Polynésie française.' },
            ].map(item => (
              <div key={item.title} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:border-[#F59E0B] transition-colors">
                <item.icon className="w-8 h-8 text-[#F59E0B] mb-4" />
                <h4 className="text-base font-bold text-white mb-2">{item.title}</h4>
                <p className="text-xs text-white/60 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ASSESSMENT */}
      <section className="py-20 bg-white" data-testid="assessment-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                ASSESSMENT
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937] mb-4">{assessment.title}</h2>
              <p className="text-gray-500 leading-relaxed mb-8">{assessment.subtitle}</p>
              <Button onClick={() => scrollTo('contact')} className="bg-[#0D9488] hover:bg-[#0F766E] text-white px-7 py-5" data-testid="cta-assessment">
                Demander un assessment <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {(assessment.cards || []).map((c) => {
                const IconComp = ICON_MAP[c.icon] || Brain;
                return (
                  <div key={c.title} className="bg-[#F8FAFC] rounded-xl p-5 border-l-[3px] border-[#0D9488]">
                    <IconComp className="w-7 h-7 text-[#0D9488] mb-3" />
                    <h4 className="text-sm font-semibold text-[#1F2937] mb-2">{c.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{c.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* JEUX DU PACIFIQUE 2027 */}
      <section className="bg-[#0D9488] py-20 relative overflow-hidden" data-testid="jdp-section">
        <div className="absolute -right-24 -top-24 w-[400px] h-[400px] rounded-full border-[50px] border-white/5" />
        <div className="absolute right-20 top-10 w-[250px] h-[250px] rounded-full border-[30px] border-[#F59E0B]/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
            {jdp.badge || 'MISSION SPECIALE'}
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">{jdp.title}</h2>
          <div className="text-white/60 max-w-xl mb-10" dangerouslySetInnerHTML={{ __html: jdp.description || '' }} />
          <div className="flex flex-wrap gap-10 mb-12">
            {[
              { num: String((jdp.postes || []).length), lbl: 'Postes ouverts' },
              { num: '5 000', lbl: 'Athletes attendus' },
              { num: '24', lbl: 'Nations participantes' },
              { num: '2027', lbl: 'Sur le Fenua' }
            ].map((s) => (
              <div key={s.lbl} className="text-center">
                <p className="text-3xl font-bold text-[#F59E0B]">{s.num}</p>
                <p className="text-xs text-white/50 mt-1">{s.lbl}</p>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {(jdp.postes || []).map((p) => (
              <div key={p.title} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 hover:border-[#F59E0B] transition-all">
                <p className="text-[#F59E0B] text-[10px] font-semibold uppercase tracking-[0.12em] mb-2">{p.dept}</p>
                <h3 className="text-lg font-bold text-white mb-2">{p.title}</h3>
                <p className="text-xs text-white/50 mb-4">{p.sub}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {(p.tags || []).map((t) => (
                    <span key={t} className="bg-white/10 text-white/70 text-[10px] px-2 py-1 rounded-full">{t}</span>
                  ))}
                </div>
                <a href={`mailto:${contact.email || 'contact@groupeaerhpolynesie.com'}`} className="text-[#F59E0B] text-sm font-semibold hover:underline">
                  Postuler <ChevronRight className="inline w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/offres">
              <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-8 py-5" data-testid="cta-all-offers">
                Voir toutes les offres d'emploi <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* OUTILS CANDIDATS */}
      <section id="apps" className="py-20 bg-[#F8FAFC]" data-testid="apps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-14">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                <Users className="w-3 h-3" /> OUTILS CANDIDATS
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937]">{tools.title}</h2>
              <p className="text-gray-500 leading-relaxed mt-4">{tools.subtitle}</p>
            </div>
            <div className="bg-white rounded-xl p-8 border border-gray-200 border-l-4 border-l-[#0D9488]">
              <h4 className="font-bold text-[#1F2937] mb-2">Pourquoi ces outils ?</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                En Polynésie française, de nombreux candidats se heurtent aux mêmes obstacles : un CV mal structuré, une lettre générique, ou un manque de visibilité. Ces applications répondent à ces freins concrets.
              </p>
            </div>
          </div>

          {/* CV BUILDER + COVER LETTER - Featured Tool */}
          <div className="mb-10 bg-white rounded-2xl overflow-hidden border-2 border-[#0D9488] shadow-lg hover:shadow-xl transition-shadow" data-testid="cv-builder-card">
            <div className="grid md:grid-cols-2">
              <div className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] p-8 md:p-10 flex flex-col justify-center relative overflow-hidden">
                <div className="absolute -right-12 -bottom-12 w-48 h-48 rounded-full border-[30px] border-white/5" />
                <div className="absolute right-16 bottom-16 w-24 h-24 rounded-full border-[15px] border-[#F59E0B]/10" />
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
                    NOUVEAU
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">CV & Lettre de motivation</h3>
                  </div>
                  <p className="text-white/70 leading-relaxed mb-6">
                    Créez votre CV professionnel et générez une lettre de motivation personnalisée par IA en quelques minutes. Tout-en-un, gratuit, avec export PDF.
                  </p>
                  <Link to="/cv-builder">
                    <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-8 py-5 text-base" data-testid="cta-cv-builder">
                      Commencer gratuitement <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <h4 className="font-bold text-[#1F2937] mb-4 text-sm uppercase tracking-wider">CV</h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 mb-6">
                  {[
                    'Wizard 10 étapes guidées',
                    '3 modèles professionnels',
                    'Aperçu en temps réel',
                    'Export PDF haute qualité',
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#0D9488] flex-shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
                <h4 className="font-bold text-[#1F2937] mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
                  Lettre de motivation
                  <span className="text-[9px] bg-[#F59E0B] text-white px-1.5 py-0.5 rounded-full font-bold">IA</span>
                </h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                  {[
                    'Génération par intelligence artificielle',
                    'Pré-remplie depuis votre CV',
                    'Personnalisée pour chaque offre',
                    'Éditable, copiable, export PDF',
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* COACH IA D'ENTRETIEN - Featured Tool */}
          <div className="mb-10 bg-white rounded-2xl overflow-hidden border-2 border-[#0F766E] shadow-lg hover:shadow-xl transition-shadow" data-testid="coach-interview-card">
            <div className="grid md:grid-cols-2">
              <div className="bg-gradient-to-br from-[#0F766E] to-[#14B8A6] p-8 md:p-10 flex flex-col justify-center relative overflow-hidden">
                <div className="absolute -right-12 -bottom-12 w-48 h-48 rounded-full border-[30px] border-white/5" />
                <div className="absolute right-16 bottom-16 w-24 h-24 rounded-full border-[15px] border-[#F59E0B]/10" />
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
                    NOUVEAU
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <Mic className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Coach IA d'entretien</h3>
                  </div>
                  <p className="text-white/80 leading-relaxed mb-6">
                    Entraînez-vous à voix haute face à un recruteur IA. Importez votre offre, choisissez un mode (entretien adaptatif ou Top 20 questions), et obtenez un coaching personnalisé à chaque réponse, avec un bilan complet en fin de session.
                  </p>
                  <Link to="/coach-entretien">
                    <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-8 py-5 text-base" data-testid="cta-coach-interview">
                      Démarrer l'entraînement <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <h4 className="font-bold text-[#1F2937] mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
                  Mode adaptatif IA
                  <span className="text-[9px] bg-[#0F766E] text-white px-1.5 py-0.5 rounded-full font-bold">IA</span>
                </h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 mb-6">
                  {[
                    'Recruteur IA réaliste',
                    'Questions adaptées au poste',
                    'Réponses vocales (micro)',
                    '10 questions enchaînées',
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#0F766E] flex-shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
                <h4 className="font-bold text-[#1F2937] mb-4 text-sm uppercase tracking-wider">Mode Top 20 questions</h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                  {[
                    'Coaching après chaque réponse',
                    'Notation 5 étoiles + conseils',
                    'Méthode STAR appliquée',
                    'Bilan final détaillé',
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Other candidate tools */}
          <div className="grid md:grid-cols-3 gap-7">
            {(tools.apps || []).filter(app => {
              const n = (app.name || '').toLowerCase();
              return !n.includes('constructeur') && !n.includes('lettre') && !n.includes('espace');
            }).map((app) => (
              <div key={app.name} className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`app-${(app.name || '').split(' ')[0].toLowerCase()}`}>
                <div className="bg-[#0D9488] p-8 text-center">
                  <h3 className="text-xl font-bold text-white mb-1">{app.name}</h3>
                  <p className="text-white/60 text-sm">{app.tagline}</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-0 mb-5">
                    {(app.features || []).map((f, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-700 py-2 border-b border-gray-100 last:border-0">
                        <ChevronRight className="w-3.5 h-3.5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {app.url ? (
                    <a href={app.url} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white">
                        {app.btn} <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </a>
                  ) : (
                    <div className="bg-[#F8FAFC] border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <p className="text-[10px] font-semibold text-[#0D9488] uppercase tracking-wider">Bientôt disponible</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEMOIGNAGES */}
      <section className="py-20 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
            <Star className="w-3 h-3" /> TEMOIGNAGES
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937] mb-10">{testimonials.title}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {(testimonials.items || []).map((t, i) => (
              <div key={i} className="bg-[#F8FAFC] rounded-xl p-8 border border-gray-200 border-t-[3px] border-t-[#0D9488]" data-testid={`testimonial-${i}`}>
                <p className="text-3xl text-[#0D9488] font-bold leading-none mb-3">"</p>
                <p className="text-sm text-gray-600 leading-relaxed italic mb-5">{t.text}</p>
                <p className="text-xs font-semibold text-[#0D9488] uppercase tracking-wider">{t.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] py-20" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                PARLONS DE VOTRE BESOIN
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{contact.title}</h2>
              <p className="text-white/60 leading-relaxed mb-10">{contact.subtitle}</p>
              <div className="space-y-5">
                {[
                  { icon: MapPin, label: 'Adresse', value: '10 Rue Georges LAGARDE - 98714 Papeete' },
                  { icon: Phone, label: 'Telephone', value: contact.phone || '87 73 78 15' },
                  { icon: Mail, label: 'Email', value: contact.email || 'contact@groupeaerhpolynesie.com' },
                  { icon: MessageSquare, label: 'WhatsApp direct', value: contact.phone || '+689 87 73 78 15' }
                ].map((c) => (
                  <div key={c.label} className="flex gap-4 items-start">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <c.icon className="w-5 h-5 text-[#F59E0B]" />
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider">{c.label}</p>
                      <p className="text-white font-medium text-sm mt-0.5">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8">
              <h3 className="text-white font-bold text-lg mb-1">Vous recrutez ?</h3>
              <p className="text-white/50 text-sm mb-6">Nous vous recontactons sous 24h ouvrées.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/60 text-xs uppercase tracking-wider">Entreprise</Label>
                    <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="Votre structure" className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/30" data-testid="form-company" />
                  </div>
                  <div>
                    <Label className="text-white/60 text-xs uppercase tracking-wider">Nom & Prenom *</Label>
                    <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Votre nom" className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/30" required data-testid="form-name" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/60 text-xs uppercase tracking-wider">Email *</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="votre@email.com" className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/30" required data-testid="form-email" />
                  </div>
                  <div>
                    <Label className="text-white/60 text-xs uppercase tracking-wider">Telephone *</Label>
                    <Input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="87 XX XX XX" className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/30" required data-testid="form-phone" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Profil recherche</Label>
                  <Input value={form.profile} onChange={(e) => setForm({...form, profile: e.target.value})} placeholder="Ex : Comptable, Technicien BTP..." className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/30" data-testid="form-profile" />
                </div>
                <div>
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Formule souhaitee</Label>
                  <Select value={form.pack} onValueChange={(v) => setForm({...form, pack: v})}>
                    <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white" data-testid="form-pack">
                      <SelectValue placeholder="Je ne sais pas encore" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non-precise">Je ne sais pas encore</SelectItem>
                      <SelectItem value="option-a">Option A — Sourcing & Qualification</SelectItem>
                      <SelectItem value="option-b">Option B — Process complet (clé en main)</SelectItem>
                      <SelectItem value="assessment">Assessment uniquement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Message complementaire</Label>
                  <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={3} placeholder="Contexte, délai, contraintes..."
                    className="mt-1 w-full rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B] resize-y"
                    data-testid="form-message" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold py-5" data-testid="form-submit">
                  {submitting ? 'Envoi en cours...' : 'Envoyer la demande'} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* WHATSAPP FLOATING */}
      <a href={`https://api.whatsapp.com/send?phone=${(contact.whatsapp || '68987737815').replace(/[^0-9]/g, '')}&text=Bonjour%20AERH%20Recrutement%20!`} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-50"
        data-testid="whatsapp-btn" title="Contactez-nous sur WhatsApp">
        <MessageSquare className="w-6 h-6 text-white" />
      </a>
    </div>
  );
};

export default RecruitmentPage;
